/**
 * App Root Component
 * Water Management System
 * 
 * This is the main application component that sets up:
 * - React Router for navigation
 * - Redux state management
 * - Authentication flow
 * - Toast notifications
 * - Layout structure (Sidebar + Header + Main Content)
 * - Protected routes
 */

import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Navigate,  useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Layout Components
import { Sidebar } from '@components/common/sidebar';
import { Header } from '@components/common/header';

// Auth Components
import { ProtectedRoute } from '@components/auth/protected_route';
import { SessionTimeoutHandler } from '@components/auth/session_timeout_handler';

// Auth Pages
import { Login } from '@pages/login';
import { ForgotPassword } from '@pages/forgot_password';
import { ResetPassword } from '@pages/reset_password';
import { Unauthorized } from '@pages/unauthorized';

// Main Pages
import { DashboardPage } from '@pages/dashboard';
import { ClientsPage } from '@pages/clients';
import { MetersPage } from '@pages/meters';
import { ReadingsPage } from '@pages/readings';
import { InvoicesPage } from '@pages/invoices';
import { PaymentsPage } from '@pages/payments';
import { ReportsPage } from '@pages/reports';
import { SettingsPage } from '@pages/settings';

// Redux
import { 
  logout, 
  get_current_user,
  select_user, 
  select_is_authenticated,
  select_user_full_name 
} from '@store/auth_slice';
import type { AppDispatch } from '@/store';

/**
 * Main Layout Component - Wraps protected routes with sidebar and header
 */
const MainLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const user = useSelector(select_user);
  const user_full_name = useSelector(select_user_full_name);
  
  // Layout state
  const [is_sidebar_collapsed, set_is_sidebar_collapsed] = useState(false);
  const [is_mobile_menu_open, set_is_mobile_menu_open] = useState(false);

  const handle_logout = async () => {
    try {
      await dispatch(logout()).unwrap();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      // Force redirect to login even if logout API fails
      navigate('/login');
    }
  };

  const handle_profile_click = () => {
    // Navigate to profile/settings page
    navigate('/settings');
  };

  const handle_settings_click = () => {
    navigate('/settings');
  };

  return (
    <div className="flex h-screen bg-off-white overflow-hidden">
      {/* Sidebar - Desktop */}
      <div className="hidden lg:block">
        <Sidebar
          is_collapsed={is_sidebar_collapsed}
          on_collapse_toggle={set_is_sidebar_collapsed}
        />
      </div>

      {/* Mobile Sidebar Overlay */}
      {is_mobile_menu_open && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={() => set_is_mobile_menu_open(false)}
          />
          
          {/* Mobile Sidebar */}
          <div className="fixed inset-y-0 left-0 z-50 lg:hidden">
            <Sidebar
              is_collapsed={false}
              on_collapse_toggle={() => set_is_mobile_menu_open(false)}
            />
          </div>
        </>
      )}

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <Header
          user_name={user_full_name || user?.username || 'User'}
          user_email={user?.email || ''}
          user_avatar={user?.profile_picture}
          notification_count={0} // TODO: Implement notification system
          on_logout={handle_logout}
          on_profile_click={handle_profile_click}
          on_settings_click={handle_settings_click}
          on_mobile_menu_toggle={() => set_is_mobile_menu_open(!is_mobile_menu_open)}
        />

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto "
          style={{
            backgroundImage: `
              linear-gradient(rgba(26, 61, 46, 0.03) 1px, transparent 1px),
              linear-gradient(90deg, rgba(26, 61, 46, 0.03) 1px, transparent 1px)
            `,
            backgroundSize: '4px 4px'
          }}
        >
          {children}
        </main>
      </div>
    </div>
  );
};

/**
 * App Router Component - Handles all routing logic
 */
const AppRouter: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const is_authenticated = useSelector(select_is_authenticated);
  // const location = useLocation();

  // Fetch current user on mount if authenticated
  useEffect(() => {
    if (is_authenticated) {
      dispatch(get_current_user()).catch((error: any) => {
        console.error('Failed to fetch current user:', error);
      });
    }
  }, [is_authenticated, dispatch]);

  return (
    <>
      {/* Session timeout handler - only active when authenticated */}
      {is_authenticated && <SessionTimeoutHandler />}
      
      <Routes>
        {/* ========== PUBLIC ROUTES ========== */}
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/unauthorized" element={<Unauthorized />} />

        {/* ========== PROTECTED ROUTES ========== */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <MainLayout>
                <Navigate to="/dashboard" replace />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Dashboard */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <MainLayout>
                <DashboardPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Clients */}
        <Route
          path="/clients"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ClientsPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Meters */}
        <Route
          path="/meters"
          element={
            <ProtectedRoute>
              <MainLayout>
                <MetersPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Readings - Requires operator or admin role */}
        <Route
          path="/readings"
          element={
            <ProtectedRoute required_roles={['admin', 'operator']}>
              <MainLayout>
                <ReadingsPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Invoices */}
        <Route
          path="/invoices"
          element={
            <ProtectedRoute>
              <MainLayout>
                <InvoicesPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Payments - Requires operator or admin role */}
        <Route
          path="/payments"
          element={
            <ProtectedRoute required_roles={['admin', 'operator']}>
              <MainLayout>
                <PaymentsPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Reports */}
        <Route
          path="/reports"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ReportsPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Settings - Admin only */}
        <Route
          path="/settings"
          element={
            <ProtectedRoute required_role="admin">
              <MainLayout>
                <SettingsPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* 404 - Catch all */}
        <Route
          path="*"
          element={
            is_authenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
      </Routes>
    </>
  );
};

/**
 * Main App Component
 * Note: This component should be wrapped in Redux Provider in main.tsx
 */
const App: React.FC = () => {
  return (
    <Router>
      <AppRouter />
      
      {/* Toast Notifications Container */}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        toastClassName="font-sans"
      />
    </Router>
  );
};

export default App;